import React, { useEffect, useState } from "react";
import { Row, Col, Card, Table, Button, Input, Modal, Typography, Tag, Space, Drawer, Form, Select, notification } from "antd";
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../config";

const { confirm } = Modal;
const { Title } = Typography;
const { Item } = Form;
const { Option } = Select;

function PopStatesList() {
  const [form] = Form.useForm();
  const [dataList, setDataList]     = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit]     = useState(15);
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId]         = useState(null);
  const [saving, setSaving]         = useState(false);

  // Filter dropdowns
  const [countries, setCountries]         = useState([]);
  const [filterCountry, setFilterCountry] = useState('');

  // Drawer — cascading states
  const [drawerStates, setDrawerStates] = useState([]);

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

  useEffect(() => {
    axios.get(`${baseUrl}/pop-country/list-all`, { headers: authHeader })
      .then(r => { if (r.data.status) setCountries(r.data.result); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchList = (page = currentPage, q = search, countryId = filterCountry) => {
    setLoading(true);
    const params = { page, limit, search: q };
    if (countryId) params.country_id = countryId;
    axios.get(`${baseUrl}/pop-states`, { headers: authHeader, params })
      .then(r => {
        if (r.data.status) {
          setDataList(r.data.result.map((item, i) => ({ ...item, key: (page - 1) * limit + i + 1 })));
          setTotalCount(r.data.totalCount);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchList(); }, [currentPage]); // eslint-disable-line

  const handleReset = () => {
    setFilterCountry('');
    setSearch('');
    setCurrentPage(1);
    fetchList(1, '', '');
  };

  // Drawer helpers
  const openAdd = () => {
    form.resetFields();
    form.setFieldsValue({ status: '1' });
    setDrawerStates([]);
    setEditId(null);
    setDrawerOpen(true);
  };

  const openEdit = (r) => {
    form.setFieldsValue({
      country_id: r.country_id ? parseInt(r.country_id) : undefined,
      name: r.name, code: r.code, status: String(r.status),
    });
    setEditId(r.id);
    setDrawerOpen(true);
  };

  const onSave = async (values) => {
    setSaving(true);
    try {
      if (editId) await axios.put(`${baseUrl}/pop-states/${editId}`, values, { headers: authHeader });
      else        await axios.post(`${baseUrl}/pop-states`, values, { headers: authHeader });
      notification.success({ message: editId ? 'State updated' : 'State added' });
      setDrawerOpen(false);
      fetchList();
    } catch (e) {
      notification.error({ message: e.response?.data?.message || 'Error saving' });
    } finally { setSaving(false); }
  };

  const handleDelete = (id) => confirm({
    title: 'Delete this state?', icon: <ExclamationCircleOutlined />,
    okText: 'Yes, Delete', okType: 'danger', cancelText: 'Cancel',
    onOk: async () => {
      await axios.delete(`${baseUrl}/pop-states/${id}`, { headers: authHeader });
      notification.success({ message: 'Deleted' });
      fetchList();
    },
  });

  const columns = [
    { title: '#', dataIndex: 'key', key: 'key', width: 60 },
    { title: 'State Name', dataIndex: 'name', key: 'name' },
    { title: 'Code', dataIndex: 'code', key: 'code', width: 80 },
    { title: 'Country', dataIndex: 'country_name', key: 'country_name' },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 90,
      render: s => <Tag color={String(s) === '1' ? 'green' : 'red'}>{String(s) === '1' ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 140,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Edit</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>Delete</Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col span={24}>
          <Card
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>States <Tag color="blue">{totalCount}</Tag></span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <Button icon={<PlusOutlined />} onClick={openAdd}
                style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', fontWeight: 600 }}>
                Add State
              </Button>
            }
          >
            {/* ── Filter Bar ── */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
              <Space wrap size={10}>
                <Select
                  placeholder="Filter by Country"
                  style={{ width: 200 }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  value={filterCountry || undefined}
                  onChange={v => {
                    setFilterCountry(v || '');
                    setCurrentPage(1);
                    fetchList(1, search, v || '');
                  }}
                >
                  {countries.map(c => <Option key={c.id} value={String(c.id)}>{c.name}</Option>)}
                </Select>

                <Input.Search
                  placeholder="Search state name..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onSearch={val => { setSearch(val); setCurrentPage(1); fetchList(1, val, filterCountry); }}
                  allowClear
                  style={{ width: 240 }}
                  enterButton={<Button icon={<SearchOutlined />} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Search</Button>}
                />

                <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
              </Space>
            </div>

            <Table
              columns={columns}
              dataSource={dataList}
              loading={loading}
              size="small"
              scroll={{ x: 'max-content' }}
              pagination={{
                current: currentPage, pageSize: limit, total: totalCount,
                onChange: p => setCurrentPage(p),
                showTotal: t => `Total ${t} states`,
                showSizeChanger: false,
              }}
            />
          </Card>
        </Col>
      </Row>

      <Drawer
        title={<span style={{ fontWeight: 700, color: '#1e293b' }}>{editId ? 'Edit State' : 'Add State'}</span>}
        width={480}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          <Space>
            <Button icon={<EditOutlined />} loading={saving} onClick={() => form.submit()}
              style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Save</Button>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Item name="country_id" label="Country" rules={[{ required: true, message: 'Required' }]}>
            <Select placeholder="Select Country" showSearch optionFilterProp="children" style={{ width: '100%' }}>
              {countries.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Select>
          </Item>
          <Item name="name" label="State Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="Enter state name" />
          </Item>
          <Item name="code" label="State Code"><Input placeholder="e.g. RJ, MH" /></Item>
          <Item name="status" label="Status">
            <Select>
              <Option value="1">Active</Option>
              <Option value="0">Inactive</Option>
            </Select>
          </Item>
        </Form>
      </Drawer>
    </div>
  );
}

export default PopStatesList;
